import {
    Box,
    Heading,
    Text,
    Tag,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Divider,
    SimpleGrid,
    Card,
    CardBody,
    HStack,
} from "@chakra-ui/react";
import { formatDateTime } from "utils/dateUtils/dateUtils";

interface SdkOrderDetailsProps {
    data: any;
}

export default function SdkOrderDetails({ data }: SdkOrderDetailsProps) {
    if (!data) return null;

    const getStatusColor = (status: string) => {
        switch (status) {
            case "SUCCESSFUL":
            case "REFUNDED":
            case "ACTIVE":
            case "COMPLETED":
            case "CLAIMED":
                return "green";
            case "PAYMENT_FAILED":
            case "CANCELLED":
                return "red";
            case "PAYMENT_INITIATED":
            case "CANCEL_INITIATED":
            case "UNCLAIMED":
                return "orange";
            default:
                return "blue";
        }
    };

    const InfoText = ({ label, value }: { label: string; value: React.ReactNode }) =>
        value ? (
            <HStack spacing={1} align="start">
                <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                    {label}:
                </Text>
                <Text fontSize={{ base: "sm", md: "md" }}>{value}</Text>
            </HStack>
        ) : null;

    return (
        <Box px={6}>
            {/* Transaction Summary */}
            <Heading size="md" mb={4}>
                Transaction Details
            </Heading>
            <Card mb={6} bg="gray.50">
                <CardBody>
                    <SimpleGrid columns={2} spacing={4}>
                        <InfoText label="Transaction ID" value={data?._id} />
                        {data?.status && (
                            <HStack spacing={1}>
                                <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                    Status:
                                </Text>
                                <Tag size={{ base: "sm", md: "md" }} colorScheme={getStatusColor(data.status)}>
                                    {data.status}
                                </Tag>
                            </HStack>
                        )}
                        <InfoText
                            label="User Name"
                            value={data?.user?.name || data?.billingDetails?.name}
                        />
                        <InfoText
                            label="User Number"
                            value={data?.user?.moNumber || data?.billingDetails?.phone}
                        />
                        <InfoText label="Email" value={data?.billingDetails?.email} />
                        <InfoText label="Tickets" value={data?.ticketCount} />
                        <InfoText label="Total Price" value={`₹ ${data?.totalPrice}`} />
                        <InfoText
                            label="Stations"
                            value={
                                data?.startStation && data?.destinationStation
                                    ? `${data.startStation} → ${data.destinationStation}`
                                    : null
                            }
                        />
                        <InfoText
                            label="Created At"
                            value={data?.createdAt && formatDateTime(data.createdAt)}
                        />
                        <InfoText
                            label="Updated At"
                            value={data?.updatedAt && formatDateTime(data.updatedAt)}
                        />
                    </SimpleGrid>
                </CardBody>
            </Card>

            <Divider my={5} />

            {/* Orders */}
            <Heading size="md" mb={3}>
                Orders
            </Heading>
            <Accordion allowMultiple>
                {data?.orders?.map((order: any, idx: number) => (
                    <AccordionItem
                        key={order._id}
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        rounded="md"
                        mb={3}
                    >
                        <h2>
                            <AccordionButton bg="gray.100" rounded="md" _expanded={{ bg: "gray.200" }}>
                                <Box flex="1" textAlign="left">
                                    {idx + 1}. {order.providerName || "Unknown Provider"}{" "}
                                    {order.startStationName && order.destinationStationName
                                        ? `– ${order.startStationName} → ${order.destinationStationName}`
                                        : ""}
                                </Box>
                                {order.status && (
                                    <Tag size={{ base: "sm", md: "md" }} colorScheme={getStatusColor(order.status)} mr={2}>
                                        {order.status}
                                    </Tag>
                                )}
                                <AccordionIcon />
                            </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                            <SimpleGrid columns={2} spacing={4}>
                                <InfoText label="Order ID" value={order.orderId} />
                                <InfoText label="Transaction ID" value={order.transaction_id} />
                                <InfoText label="Provider ID" value={order.providerId} />
                                <InfoText label="Item" value={order.itemName} />
                                <InfoText
                                    label="Price per Ticket"
                                    value={order.pricePerTicket && `₹${order.pricePerTicket}`}
                                />
                                <InfoText
                                    label="Total Price"
                                    value={order.totalPrice && `₹${order.totalPrice}`}
                                />
                                {order.status && (
                                    <HStack spacing={1}>
                                        <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                            Status:
                                        </Text>
                                        <Tag size={{ base: "sm", md: "md" }} colorScheme={getStatusColor(order.status)}>
                                            {order.status}
                                        </Tag>
                                    </HStack>
                                )}
                            </SimpleGrid>

                            {/* Tickets */}
                            {order.tickets?.length > 0 && (
                                <Box mt={4}>
                                    <Text fontWeight="bold" mb={2} fontSize={{ base: "sm", md: "md" }}>
                                        Tickets
                                    </Text>
                                    <SimpleGrid columns={2} spacing={3}>
                                        {order.tickets.map((t: any) => (
                                            <Card key={t._id} borderWidth="1px" shadow="sm">
                                                <CardBody fontSize={{ base: "sm", md: "md" }}>
                                                    <InfoText label="ID" value={t.ticketId} />
                                                    {t.status && (
                                                        <HStack spacing={1}>
                                                            <Text fontWeight="bold" fontSize={{ base: "sm", md: "md" }}>
                                                                Status:
                                                            </Text>
                                                            <Tag size={{ base: "sm", md: "md" }} colorScheme={getStatusColor(t.status)}>
                                                                {t.status}
                                                            </Tag>
                                                        </HStack>
                                                    )}
                                                    <InfoText
                                                        label="Valid Till"
                                                        value={t.validTill && formatDateTime(t.validTill)}
                                                    />
                                                </CardBody>
                                            </Card>
                                        ))}
                                    </SimpleGrid>
                                </Box>
                            )}
                        </AccordionPanel>
                    </AccordionItem>
                ))}
            </Accordion>
        </Box>
    );
}
